// ============================================================================
// JARVISTRAVEL - CLERK SESSION TASKS (JAR-1700)
// Where a session that is signed in but not finished has to go next.
// ============================================================================

/** The shape of a session this module needs, which is all it reads. */
type SessionWithTask = { currentTask?: { key?: string } } | null | undefined;

/** The screen that enrols the second factor Clerk is waiting for. */
export const SETUP_MFA_PATH = '/auth/setup-mfa';

/**
 * The path a session's outstanding task has to be finished on, or null.
 *
 * The production instance requires MFA at sign-up (`sign_up.mfa.required`), and
 * Clerk does not refuse the credential for it: it creates the session in a
 * PENDING state carrying a task, and nothing about the app's own routing will
 * take them to finish it. Without this the person completes every step and is
 * settled into the product with the task undone.
 *
 * WHICH HOOK SEES WHAT, measured against @clerk/shared rather than assumed (an
 * earlier version of these comments had it backwards):
 *
 *   - `useAuth` treats a pending session as signed OUT, by default —
 *     resolveAuthState checks `treatPendingAsSignedOut && sessionStatus ===
 *     "pending"`.
 *   - `useUser` has no such check. It returns the user, `isSignedIn: true`.
 *
 * App's routing reads `useUser` (App.tsx), so to App a pending user is signed
 * IN. That is what makes an unrouted task dangerous rather than merely stuck.
 *
 * `choose-organization` and `reset-password` are Clerk's other two task keys.
 * Neither is reachable here: this app creates no organizations, and a password
 * reset finishes inside ForgotPasswordScreen. They are left unhandled rather
 * than routed somewhere plausible, so turning either on in the dashboard
 * surfaces as a visible dead end instead of a silent wrong turn.
 */
export function taskPath(session: SessionWithTask): string | null {
  return session?.currentTask?.key === 'setup-mfa' ? SETUP_MFA_PATH : null;
}

/**
 * The `navigate` callback for `setActive`, which Clerk calls with the session it
 * just activated, PLUS the answer to "did it route?".
 *
 * The caller needs that answer. Every screen that activates a session also
 * navigates on its own afterwards (a saved link, /trips, /onboarding), and an
 * unconditional navigation lands on top of the task redirect Clerk just asked
 * for. The person is carried off the enrolment screen onto /trips or
 * /onboarding — and App's routing, which reads `useUser` and so sees a pending
 * session as signed in, leaves them there with the task never finished. The
 * gap this ticket exists to close, reopened one screen later (web-app#440
 * review, HIGH).
 *
 * Returning without navigating leaves the caller's own routing in charge, which
 * is what every non-task sign-in wants.
 *
 * Typed by what it reads rather than by Clerk's own callback type, which this
 * version of the SDK does not export from a path an app can import.
 */
export function taskRouter(go?: (path: string) => void) {
  let routed = false;
  return {
    navigate: async ({ session }: { session?: SessionWithTask }) => {
      const path = taskPath(session);
      // No handler is no routing, so `routed` must stay false. Taking the
      // caller's navigation away when nothing replaced it would leave the
      // person on a screen that has already finished, which is worse than the
      // bug above: the caller is then the only thing that can move them.
      if (!path || !go) return;
      routed = true;
      go(path);
    },
    /** True once a task redirect has been issued, so the caller stands down. */
    get routed() {
      return routed;
    },
  };
}
